/** Base URL for HTTP API calls (override via APP_CONFIG provider in production). */
export const DEFAULT_API_BASE_URL = '/api';

/** REST paths relative to {@link DEFAULT_API_BASE_URL}. */
export const API_PATHS = {
  /** POST: authenticate user and receive JWT + roles. */
  loginUser: '/loginUser',
  /** POST: invalidate the current session (requires Bearer token). */
  logOutUser: '/logOutUser',
  /** POST: flight search (Travelport / aggregator). */
  flightSearch: '/flightSearch',
  /** POST: seat map for one offer ({ bookingMeta }, the offer's own bookingMeta object). */
  flightSeatMap: '/flightSeatMap',
  /** POST: hotel search (Travelport / aggregator). */
  searchHotel: '/searchHotel',
  /** POST: car rental search (Duffel Cars; coordinates only). */
  vehicleSearch: '/vehicleSearch',
  /** POST: typeahead location lookup (cities/airports) for hotel search. */
  getLocations: '/getLocations',
  /** POST: paginated user list with search and filters. */
  getAllUsers: '/getAllUsers',
  /** POST: fetch a single user by id (for editing). */
  getUserById: '/getUserById',
  /** POST: create a new user account. */
  createUser: '/createUser',
  /** POST: update an existing user (partial; requires id). */
  updateUser: '/updateUser',
  /** POST: delete a user ({ id }). */
  deleteUser: '/deleteUser',
  /** POST: activate/deactivate a user ({ id, status_id }). */
  updateUserStatus: '/updateUserStatus',
  /** POST: paginated list of available roles for user assignment. */
  getAllRoles: '/getAllRoles',
  /** POST: paginated department list. */
  getDepartments: '/getDepartments',
  /** POST: paginated designation list. */
  getDesignations: '/getDesignations',
  /** POST: paginated region list. */
  getRegions: '/getRegions',
  /** POST: paginated country list (optionally scoped by region_id). */
  getCountries: '/getCountries',
  /** POST: paginated dialing codes; search matches country, ISO code or number. */
  getAllPhoneCodes: '/getAllPhoneCodes',
  /** POST: paginated list of approver matrices (approval hierarchy). */
  getAllApproverMatrices: '/getAllApproverMatrices',
  /** POST: create an approver matrix with its approver levels. */
  addApproverMatrix: '/addApproverMatrix',
  /** POST: update an existing approver matrix ({ id, ...fields, approver_levels }). */
  updateApproverMatrix: '/updateApproverMatrix',
  /** POST: delete an approver matrix ({ id }). */
  deleteApproverMatrix: '/deleteApproverMatrix',
  /** POST: paginated currency list. */
  getAllCurrencies: '/getAllCurrencies',
  /** POST: paginated list of advance requests for the current user. */
  getAllAdvanceRequests: '/getAllAdvanceRequests',
  /** POST: fetch a single advance request by id (for view/edit). */
  getAdvanceRequestById: '/getAdvanceRequestById',
  /** POST: create a new advance request linked to a travel request. */
  createAdvanceRequest: '/createAdvanceRequest',
  /** POST: update an existing advance request. */
  updateAdvanceRequest: '/updateAdvanceRequest',
  /** POST: delete an advance request ({ id }). */
  deleteAdvanceRequest: '/deleteAdvanceRequest',
  /** POST: upload a receipt image/PDF and run OCR analysis. */
  uploadReceipt: '/uploadReceipt',
  /** POST: create a new expense claim (status_id 1 = draft). */
  createExpenseClaim: '/createExpenseClaim',
  /** POST: update an existing expense claim. */
  updateExpenseClaim: '/updateExpenseClaim',
  /** POST: list all expense claims for the current user. */
  getAllExpenseClaims: '/getAllExpenseClaims',
  /** POST: fetch a single expense claim by id (for view/edit). */
  getExpenseClaimById: '/getExpenseClaimById',
  /** POST: delete an expense claim ({ id }). */
  deleteExpenseClaim: '/deleteExpenseClaim',
  /** POST: expense categories — the Expense Type dropdown and the expense policy fields. */
  getExpenseList: '/getExpenseList',
  updateProfile: '/updateProfile',
  getCompanyInformation: '/getCompanyInformation',
  updateCompanyInformation: '/updateCompanyInformation',
  getUserCards: '/getUserCards',
  addUserCard: '/addUserCard',
  deleteUserCard: '/deleteUserCard',
  updateUserCard: '/updateUserCard',
  getTravelPreference: '/getTravelPreference',
  updateTravelPreference: '/updateTravelPreference',
  getFrequentTravelMemberships: '/getFrequentTravelMemberships',
  addFrequentTravelMembership: '/addFrequentTravelMembership',
  updateFrequentTravelMembership: '/updateFrequentTravelMembership',
  deleteFrequentTravelMembership: '/deleteFrequentTravelMembership',
  getUserPassports: '/getUserPassports',
  addUserPassport: '/addUserPassport',
  updateUserPassport: '/updateUserPassport',
  deleteUserPassport: '/deleteUserPassport',
  getUserVisas: '/getUserVisas',
  addUserVisa: '/addUserVisa',
  updateUserVisa: '/updateUserVisa',
  deleteUserVisa: '/deleteUserVisa',
  getAllTravelRequests: '/getAllTravelRequests',
  getDelegateTravelRequests: '/getDelegateTravelRequests',
  updateTravelRequest: '/updateTravelRequest',
  getTravelRequestById: '/getTravelRequestById',
  createTravelRequest: '/createTravelRequest',
  deleteTravelRequest: '/deleteTravelRequest',
  addToCart: '/addToCart',
  removeFromCart: '/removeFromCart',
  /** POST: paginated pending approvals for the current approver ({ category, status_id, search, page_no, limit }). */
  getPendingApprovals: '/getPendingApprovals',
  /** POST: approve/reject an approval assignment ({ approval_id, action_status_id, comments, rejection_reason }). */
  processApprovalAction: '/processApprovalAction',
  /** POST: comments for a request ({ travel_request_id | expense_claim_id | advance_request_id }). */
  getRequestComments: '/getRequestComments',
  /** POST: add a comment to a request ({ <request_id>, comment_text, comment_type }). */
  addRequestComment: '/addRequestComment',
  /** POST: admin travel report (search, filters, date range, pagination). */
  getTravelReport: '/getTravelReport',
  /** POST: admin expense report (search, filters, date range, pagination). */
  getExpenseReport: '/getExpenseReport',
  /** POST: admin approval report (summary KPIs, filters, date range, pagination). */
  getApprovalReport: '/getApprovalReport',
  /** POST: admin advance report (search, status, date range, pagination). */
  getAdvanceReport: '/getAdvanceReport',
  /** POST: create a travel & expense policy pack. */
  addPolicy: '/addPolicy',
  /** POST: paginated policy list (search, is_active, page, limit). */
  getAllPolicies: '/getAllPolicies',
  /** POST: fetch a single policy by id (for view/edit). */
  getPolicyById: '/getPolicyById',
  /** POST: update an existing policy pack. */
  updatePolicy: '/updatePolicy',
  /** POST: soft-delete a policy ({ id }). */
  deletePolicy: '/deletePolicy',
  /** POST: current user's notifications ({ status, limit, offset }). */
  getMyNotifications: '/getMyNotifications',
  /** POST: mark a single notification as read ({ id }). */
  markNotificationAsRead: '/markNotificationAsRead',
  /** POST: mark every notification for the current user as read. */
  markAllNotificationsAsRead: '/markAllNotificationsAsRead',
} as const;

export const STORAGE_KEYS = {
  accessToken: 'tne_access_token',
  /** Serialized {@link AuthUser} (token stored separately in accessToken). */
  user: 'tne_user',
  /** Active role id from the login response (portal is derived from this). */
  activeRoleId: 'tne_active_role_id',
} as const;
